// src/components/map/hooks/adapters/useContributionHighlight.ts
//
// Sub-hook for useFacilityHighlightAdapter.
// Manages contribution well highlighting: fetching contributing-well data,
// rendering highlighted well layers (horizontal + vertical), legend state,
// filter dispatch actions, and the Redux-driven auto-highlight trigger.
//
// The consuming adapter is responsible for composing this with the click
// handler (which owns clickedEntity) and the network map sub-hook.
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { feature, featureCollection } from "@turf/helpers";
import colormap from "colormap";
import type { AnyLayer, LngLatBoundsLike } from "mapbox-gl";
import { useMapStore } from "stores/map";
import { selectHighlightContributingWellsForFacility } from "stores/map/mapSelectors";
import { MapInteraction } from "types/maps/interactions";

import { WELL_LAYER, WELL_LAYER_POINT } from "../../../../constants";
import { filterWellsFromList } from "../../../../store/features";
import {
  filterWellsFromList as filterFacilitiesFromList,
  setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter
} from "../../../../store/features/filter/facilityFilterSlice";
import { setIsMidstreamNavigatorFilter } from "../../../../store/features/filter/filterSlice";
import { panToNetworkBounds } from "../../toolbar/midstream-navigator/hooks/util";
import { waitForStyleToLoad } from "../../utils";
import { clearGeojsonSource, setGeojsonSource } from "../../utils/geojsonSource";
import useGetContributingWells from "../useGetContributingWellsForFacilities";
import useGetFacilityContributingWellsNetwork from "../useGetFacilityContributingWellsNetwork";
import useGetFacilityFromWell from "../useGetFacilityFromWell";
import useGetFacilityNetworkFraction, {
  NetworkFraction
} from "../useGetFacilityNetworkFraction";
import { useMapContext } from "../useMapContext";
import { useMapDispatch } from "../useMapDispatch";
import useMutateFacilitiesEnvelope from "../useMutateFacilitiesEnvelope";
import useMutateWellsEnvelope from "../useMutateWellsEnvelope";
import type { ClickedEntity } from "./useFacilityClickHandler";

// ── Constants ────────────────────────────────────────────────────────────────

const HIGHLIGHTED_WELLS = "highlighted-wells";
const HIGHLIGHTED_VERTICAL_WELLS = "highlighted-vertical-wells";
const CLICKED_FACILITY = "clicked-facility";
const LEGEND_TITLE = "Current Month Well Contribution Percent";

// ── Types ────────────────────────────────────────────────────────────────────

export interface LegendState {
  readonly legendItems: ReadonlyArray<{ readonly title: string; readonly color: string }>;
  readonly visible: boolean;
  readonly title: string;
}

export interface UseContributionHighlightParams {
  /** The currently clicked entity (from useFacilityClickHandler). */
  readonly clickedEntity: Maybe<ClickedEntity>;
  /** Update the clicked entity (for Redux-driven auto-highlight triggers). */
  readonly setClickedEntity: (entity: Maybe<ClickedEntity>) => void;
}

export interface UseContributionHighlightReturn {
  /** Current contribution legend state. */
  readonly legend: LegendState;
  /** Highlight wells contributing to the currently clicked facility. */
  readonly highlightContributionWells: () => void;
  /** Clear the contribution legend. */
  readonly clearLegend: () => void;
  /** Filter map to contributing wells for the clicked entity. */
  readonly filterToContributingWells: () => void;
  /** Filter map to facilities for the clicked well. */
  readonly filterToFacilities: () => Promise<void>;
  /** Filter to all contributing wells across all facilities in current groups. */
  readonly filterToAllContributingWellsFromAllFacilities: () => Promise<void>;
  /** Filter to the full network for all contributing wells from the clicked facility. */
  readonly filterToNetworkForAllContributingWells: () => Promise<void>;
  /** Whether contributing-well data is loading (for loading indicator). */
  readonly isContributionLoading: boolean;
  /** Whether contributing-well data is fetching. */
  readonly isContributionFetching: boolean;
  /** Whether facility-from-well data is fetching. */
  readonly isWellFetching: boolean;
  /** Set midstream navigator filter flags before dispatching filters. */
  readonly setMidstreamFilterFlags: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Sub-hook that manages contribution well highlighting on the Mapbox map.
 *
 * Responsibilities:
 * - Fetches contributing-well data via React Query when a facility is clicked
 * - Renders highlighted well layers (horizontal line + vertical circle)
 * - Manages the contribution color legend (title, items, visibility)
 * - Dispatches filter actions (wells, facilities) to Redux
 * - Listens for Redux-driven auto-highlight triggers from the midstream navigator
 * - Dispatches loading state to the map context
 */
export function useContributionHighlight(
  params: UseContributionHighlightParams
): UseContributionHighlightReturn {
  const { clickedEntity, setClickedEntity } = params;

  // ── Map context ────────────────────────────────────────────────────

  const { mapbox, facilitiesInGroup } = useMapContext();

  // ── Store reads / actions ──────────────────────────────────────────

  const highlightContributingWellsForFacility = useMapStore(
    selectHighlightContributingWellsForFacility
  );
  const mapDispatch = useMapDispatch();
  const dispatch = useDispatch();

  // ── React Query hooks ──────────────────────────────────────────────

  const getContributingWells = useGetContributingWells();
  const getFacilityContributingWellsNetwork = useGetFacilityContributingWellsNetwork();

  const {
    data: wellData,
    isLoading,
    isFetching
  } = useGetFacilityNetworkFraction(clickedEntity?.facility?.facilityId);

  const { data: facilityData, isFetching: isWellFetching } = useGetFacilityFromWell(
    clickedEntity?.well?.wellId
  );

  const { mutateAsync: getWellsEnvelope } = useMutateWellsEnvelope();
  const { mutateAsync: getFacilitiesEnvelope } = useMutateFacilitiesEnvelope();

  // ── Local state ────────────────────────────────────────────────────

  const [legend, setLegend] = useState<LegendState>({
    legendItems: [],
    visible: false,
    title: LEGEND_TITLE
  });

  // ── Legend management ──────────────────────────────────────────────

  const clearLegend = useCallback(() => {
    setLegend({ legendItems: [], title: LEGEND_TITLE, visible: false });
  }, []);

  // ── Highlight contribution wells ───────────────────────────────────

  const highlightContributionWells = useCallback(() => {
    if (!mapbox || !wellData) return;

    function clearHighlightedWells() {
      clearGeojsonSource(mapbox, HIGHLIGHTED_WELLS);
      clearGeojsonSource(mapbox, HIGHLIGHTED_VERTICAL_WELLS);
    }

    function setHighlightedHzWells(
      collection: GeoJSON.FeatureCollection,
      stops: (string | number)[][]
    ) {
      if (!setGeojsonSource(mapbox, HIGHLIGHTED_WELLS, collection)) {
        const layer = {
          id: HIGHLIGHTED_WELLS,
          type: "line",
          source: { type: "geojson", data: collection },
          paint: {
            "line-color": { property: "value", stops },
            "line-width": 5
          }
        } as AnyLayer;
        const beforeLayer = mapbox.getLayer(CLICKED_FACILITY)
          ? CLICKED_FACILITY
          : undefined;
        mapbox.addLayer(layer, beforeLayer);
      } else {
        mapbox.setPaintProperty(HIGHLIGHTED_WELLS, "line-color", {
          property: "value",
          stops
        });
      }
    }

    function setHighlightedVerticalWells(
      collection: GeoJSON.FeatureCollection,
      stops: (string | number)[][]
    ) {
      if (!setGeojsonSource(mapbox, HIGHLIGHTED_VERTICAL_WELLS, collection)) {
        const layer = {
          id: HIGHLIGHTED_VERTICAL_WELLS,
          type: "circle",
          source: { type: "geojson", data: collection },
          paint: {
            "circle-color": { property: "value", stops },
            "circle-radius": 5
          }
        } as AnyLayer;
        const beforeLayer = mapbox.getLayer(CLICKED_FACILITY)
          ? CLICKED_FACILITY
          : undefined;
        mapbox.addLayer(layer, beforeLayer);
      } else {
        mapbox.setPaintProperty(HIGHLIGHTED_VERTICAL_WELLS, "circle-color", {
          property: "value",
          stops
        });
      }
    }

    async function showWellsContributingToFacility() {
      if (!mapbox.isStyleLoaded()) {
        await waitForStyleToLoad(mapbox, 1000);
      }
      clearHighlightedWells();

      if (wellData.length === 0) {
        setLegend({ legendItems: [], title: LEGEND_TITLE, visible: false });
        toast.warn("No contributing wells found for selected facility.");
        return;
      }

      let envelope: Maybe<{
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
      }> = null;

      try {
        envelope = await getWellsEnvelope(clickedEntity?.facility?.facilityId);
        if (
          !envelope ||
          envelope.minY === 0 ||
          envelope.minX === 0 ||
          envelope.maxX === 0 ||
          envelope.maxY === 0 ||
          envelope.minX === envelope.maxX ||
          envelope.minY === envelope.maxY
        ) {
          toast.warn("No contributing wells found for selected facility.");
          return;
        }
      } catch {
        toast.error("Error fetching contributing wells");
        return;
      }

      const bounds = [
        [envelope.minX, envelope.minY],
        [envelope.maxX, envelope.maxY]
      ] as LngLatBoundsLike;

      await new Promise<void>((resolve) => {
        mapbox.once(MapInteraction.MoveEnd, () => {
          mapbox.once(MapInteraction.Idle, () => resolve());
        });
        mapbox.fitBounds(bounds, { padding: 50, duration: 3 });
      });

      // Wait for the map to load data from cache/server to tiles
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1500);
      });

      const min = 0;
      const max = 100;
      const ids = wellData.reduce((obj, item) => {
        obj[item.wellId] = item.gasPct;
        return obj;
      }, {} as Record<string, number>);

      const steps = (max - min) / 5.0;
      const colors = colormap({
        colormap: "hot",
        nshades: 7,
        format: "hex",
        alpha: 1
      })
        .reverse()
        .slice(1);

      const legendItems: { title: string; color: string }[] = [];
      const stops = Array.from({ length: 5 }, (_v, i) => {
        legendItems.push({
          title: `${Math.round(min + i * steps).toLocaleString()} - ${Math.round(
            min + (i + 1) * steps
          ).toLocaleString()}`,
          color: colors[i]
        });
        return [min + i * steps, colors[i]];
      });

      setLegend({ legendItems, title: LEGEND_TITLE, visible: true });

      // Check if well layers are available before querying them
      if (!mapbox.getLayer(WELL_LAYER) || !mapbox.getLayer(WELL_LAYER_POINT)) {
        const waitForLayers = () => {
          if (mapbox.getLayer(WELL_LAYER) && mapbox.getLayer(WELL_LAYER_POINT)) {
            showWellsContributingToFacility();
          } else {
            mapbox.once(MapInteraction.Idle, waitForLayers);
          }
        };
        mapbox.once(MapInteraction.Idle, waitForLayers);
        return;
      }

      const foundHzWells = mapbox.querySourceFeatures(WELL_LAYER, {
        sourceLayer: "eva-wells",
        filter: ["has", ["get", "Uwi"], ["literal", ids]]
      });

      const foundVerticalWells = mapbox.querySourceFeatures(WELL_LAYER_POINT, {
        sourceLayer: "eva-wells",
        filter: ["has", ["get", "Uwi"], ["literal", ids]]
      });

      const hzFeatures = foundHzWells.map((feat) =>
        feature(feat.geometry, {
          ...feat.properties,
          value: ids[feat.properties?.Uwi]
        })
      );
      const vFeatures = foundVerticalWells.map((feat) =>
        feature(feat.geometry, {
          ...feat.properties,
          value: ids[feat.properties?.Uwi]
        })
      );

      setHighlightedHzWells(featureCollection(hzFeatures), stops);
      setHighlightedVerticalWells(featureCollection(vFeatures), stops);
    }

    showWellsContributingToFacility();
  }, [wellData, mapbox, clickedEntity?.facility?.facilityId, getWellsEnvelope]);

  // ── Filter actions ─────────────────────────────────────────────────

  const filterToContributingWells = useCallback(() => {
    if (clickedEntity?.well) {
      dispatch(
        filterWellsFromList({
          wellList: [clickedEntity.well.wellId],
          resetMidstreamFlag: false
        })
      );
      return;
    }
    if (wellData && clickedEntity?.facility) {
      const uwids = wellData.map((item) => item.wellId);
      if (uwids.length === 0) {
        toast.warn("No contributing wells found for selected facility.");
        return;
      }
      dispatch(filterWellsFromList({ wellList: uwids, resetMidstreamFlag: false }));
    }
  }, [clickedEntity, wellData, dispatch]);

  const filterToFacilities = useCallback(async () => {
    if (clickedEntity?.facility) {
      dispatch(
        filterFacilitiesFromList({
          wellList: [clickedEntity.facility.facilityId],
          resetMidstreamFlag: false
        })
      );
    } else if (clickedEntity?.well) {
      const facilities = facilityData?.map((item) => item.facilityId) ?? [];
      if (facilities.length === 0) {
        toast.warn("No sales facilities found for selected well.");
        return;
      }
      dispatch(
        filterFacilitiesFromList({ wellList: facilities, resetMidstreamFlag: false })
      );
      const wellId = clickedEntity.well.wellId;
      const envelope = await getFacilitiesEnvelope({ wellId, facilityIds: facilities });
      await panToNetworkBounds(wellId, facilities, mapbox, envelope);
    }
  }, [clickedEntity, facilityData, dispatch, getFacilitiesEnvelope, mapbox]);

  const filterToAllContributingWellsFromAllFacilities = useCallback(async () => {
    const newFacilityList: string[] = [];
    for (const title of Object.keys(facilitiesInGroup)) {
      const group = facilitiesInGroup[title];
      for (const id of Object.keys(group.uwis)) {
        newFacilityList.push(id);
      }
    }

    try {
      const contributingWells = await getContributingWells.mutateAsync(newFacilityList);
      if (contributingWells) {
        const wellIds = contributingWells.map((well) => well.wellId);
        if (wellIds.length === 0) {
          toast.warn("No contributing wells found for facilities.");
          return;
        }
        dispatch(filterWellsFromList({ wellList: wellIds, resetMidstreamFlag: false }));
        dispatch(
          filterFacilitiesFromList({
            wellList: newFacilityList,
            resetMidstreamFlag: false
          })
        );
      }
    } catch {
      toast.error("Failed to fetch contributing wells.");
    }
  }, [facilitiesInGroup, getContributingWells, dispatch]);

  const filterToNetworkForAllContributingWells = useCallback(async () => {
    try {
      const facilityNetwork: NetworkFraction[] =
        await getFacilityContributingWellsNetwork.mutateAsync(
          clickedEntity?.facility?.facilityId
        );
      if (facilityNetwork) {
        const uniqueWellIds = [...new Set(facilityNetwork.map((well) => well.wellId))];
        const uniqueFacilityIds = [
          ...new Set(facilityNetwork.map((facility) => facility.destinationFacility))
        ];
        if (uniqueWellIds.length === 0) {
          toast.warn("No facility network found.");
          return;
        }
        dispatch(
          filterWellsFromList({ wellList: uniqueWellIds, resetMidstreamFlag: false })
        );
        dispatch(
          filterFacilitiesFromList({
            wellList: uniqueFacilityIds,
            resetMidstreamFlag: false
          })
        );
      }
    } catch {
      toast.error("Failed to fetch facility network.");
    }
  }, [
    clickedEntity?.facility?.facilityId,
    getFacilityContributingWellsNetwork,
    dispatch
  ]);

  const setMidstreamFilterFlags = useCallback(() => {
    dispatch(setIsMidstreamNavigatorFilter(true));
    dispatch(setIsMidstreamNavigatorFacilityFilter(true));
  }, [dispatch]);

  // ── Effect: loading state dispatch ─────────────────────────────────

  useEffect(() => {
    mapDispatch({
      type: "update",
      payload: {
        isLoading: isLoading || isFetching || isWellFetching
      }
    });
  }, [isLoading, isFetching, isWellFetching, mapDispatch]);

  // ── Effect: Redux trigger — highlight contributing wells ────────────

  useEffect(() => {
    if (highlightContributingWellsForFacility) {
      const tempClickedEntity: ClickedEntity = {
        facility: {
          facilityId: highlightContributingWellsForFacility,
          geometry: null
        },
        well: null
      };
      setClickedEntity(tempClickedEntity);
    }
  }, [highlightContributingWellsForFacility, setClickedEntity]);

  // ── Effect: clear when Redux highlight is removed ──────────────────

  useEffect(() => {
    if (highlightContributingWellsForFacility === null) {
      clearLegend();
      clearGeojsonSource(mapbox, HIGHLIGHTED_WELLS);
      clearGeojsonSource(mapbox, HIGHLIGHTED_VERTICAL_WELLS);
      if (clickedEntity?.facility?.geometry === null) {
        setClickedEntity({ facility: null, well: null });
      }
    }
  }, [
    highlightContributingWellsForFacility,
    clickedEntity?.facility?.geometry,
    mapbox,
    clearLegend,
    setClickedEntity
  ]);

  // ── Effect: auto-highlight when Redux-triggered entity data arrives ──

  useEffect(() => {
    if (
      clickedEntity?.facility?.geometry === null &&
      wellData &&
      !isLoading &&
      !isFetching
    ) {
      highlightContributionWells();
    }
  }, [clickedEntity, wellData, highlightContributionWells, isLoading, isFetching]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    legend,
    highlightContributionWells,
    clearLegend,
    filterToContributingWells,
    filterToFacilities,
    filterToAllContributingWellsFromAllFacilities,
    filterToNetworkForAllContributingWells,
    isContributionLoading: isLoading,
    isContributionFetching: isFetching,
    isWellFetching,
    setMidstreamFilterFlags
  };
}
